import { useState, useEffect } from 'react';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';
import { formatActualDateTime } from '../../utils/dateUtils';

/**
 * Challenges Activity Component
 * Clean UI design with text input for challenges
 */

const ChallengesActivity = ({
  activity,
  sessionId,
  sessionCode,
  sessionName = 'Live Session',
  trainerName = 'Trainer',
  timeRemaining,
  onSubmit,
  onBackToList,
  showResults = false,
  userChallenge = null,
  isActivityEnded = false,
}) => {
  // Draft key — scoped to (sessionId, activityId) so different activities
  // / sessions never bleed into each other. Returns null if either is
  // missing (then persistence is silently skipped).
  const activityKeyId = activity?._id || activity?.id;
  const draftKey = sessionId && activityKeyId
    ? `pending_challenges_${sessionId}_${activityKeyId}`
    : null;

  // Support both single text and array of challenges. Lazy-initialize from
  // sessionStorage so a participant who accidentally taps away mid-typing
  // (mobile thumb-zone collision with the bottom nav) gets all their
  // in-progress text + boxes back on remount.
  const [challenges, setChallenges] = useState(() => {
    if (draftKey && !userChallenge) {
      try {
        const saved = sessionStorage.getItem(draftKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
      } catch (_) { /* malformed JSON / quota / private mode — ignore */ }
    }
    return [''];
  });

  // Update state when userChallenge prop changes (e.g., when returning to a responded activity)
  useEffect(() => {
    if (userChallenge) {
      // Handle both array and single text formats
      if (Array.isArray(userChallenge.challenges) && userChallenge.challenges.length > 0) {
        setChallenges(userChallenge.challenges);
      } else if (userChallenge.text) {
        // If it's a single text, check if it contains multiple lines that could be challenges
        const text = userChallenge.text;
        setChallenges([text]);
      } else if (userChallenge.challenge) {
        setChallenges([userChallenge.challenge]);
      }
    }
  }, [userChallenge]);

  // Persist in-progress draft on every change. Skipped once the activity
  // has been submitted (showResults) or ended (isActivityEnded) — at that
  // point the user can no longer edit, so storage is just stale weight.
  useEffect(() => {
    if (!draftKey) return;
    if (showResults || isActivityEnded) return;
    try {
      sessionStorage.setItem(draftKey, JSON.stringify(challenges));
    } catch (_) { /* quota / private mode — silently fall through */ }
  }, [challenges, draftKey, showResults, isActivityEnded]);

  // Use functional setState everywhere — closure-captured `challenges` from
  // an older render can clobber later updates when the user types fast or
  // edits a previous box after adding new ones, silently dropping content.
  const addChallenge = () => {
    setChallenges((prev) => (prev.length < 10 ? [...prev, ''] : prev));
  };

  const removeChallenge = (index) => {
    setChallenges((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const updateChallenge = (index, value) => {
    setChallenges((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  // Get non-empty challenges for submission
  const getNonEmptyChallenges = () => challenges.filter(c => c.trim());

  // Legacy single text for backward compatibility (concatenated view)
  const challengeText = challenges.join('\n\n');

  // Format time as M:SS
  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate timer progress using activity's actual duration
  // timeLimit is in seconds, duration is in minutes (convert to seconds)
  const maxTime = activity?.timerEnabled === true
    ? (activity?.timeLimit || (activity?.duration ? activity.duration * 60 : null) || activity?.config?.timeLimit || 0)
    : 0;
  const timerEnabled = maxTime > 0;

  const handleSubmit = () => {
    const nonEmptyChallenges = getNonEmptyChallenges();
    if (nonEmptyChallenges.length > 0) {
      // Clear the in-progress draft — submission has been attempted, the
      // form will swap to the results view, and the user can no longer edit.
      if (draftKey) {
        try { sessionStorage.removeItem(draftKey); } catch (_) {}
      }
      onSubmit({
        challenges: nonEmptyChallenges, // Array of challenges
        text: nonEmptyChallenges.join('\n\n'), // Concatenated text for backward compatibility
        timestamp: new Date().toISOString(),
      });
    }
  };

  // Activity Ended but Not Responded View
  if (isActivityEnded && !showResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                  <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                  <span className="text-xs font-medium text-neutral-600">Ended</span>
                </div>
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">CHALLENGES</h2>
          </div>
        </div>

        {/* Not Attended Message */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-neutral-100 border border-neutral-300 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-neutral-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-neutral-700 mb-2">Activity Ended</h2>
            <p className="text-neutral-600 mb-4">You didn't attend this challenges activity.</p>
            <p className="text-sm text-neutral-500">This activity has already ended, so you can no longer submit a response.</p>
            {onBackToList && (
              <button
                onClick={onBackToList}
                className="mt-6 px-6 py-2 bg-neutral-600 hover:bg-neutral-700 text-white font-semibold rounded-lg transition-colors"
              >
                Back to Activities
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (showResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isActivityEnded ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                    <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                    <span className="text-xs font-medium text-neutral-600">Ended</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-green-700">Live</span>
                  </div>
                )}
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">CHALLENGES</h2>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Success Message */}
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-green-800">Challenge Submitted!</h2>
                <p className="text-sm sm:text-base text-green-700">
                  {isActivityEnded
                    ? 'This activity has ended. Your challenge has been recorded.'
                    : 'Your trainer will review and provide guidance'}
                </p>
              </div>
            </div>
          </div>

          {/* Challenge Card */}
          <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-neutral-900 mb-4">{activity.title}</h3>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-neutral-700">Your Challenges</h4>
                  <span className="text-xs text-neutral-500">
                    {((userChallenge?.challenges || []).length > 0 ? userChallenge.challenges : challenges).filter(c => c?.trim()).length} challenge(s)
                  </span>
                </div>
                <div className="space-y-3">
                  {((userChallenge?.challenges && userChallenge.challenges.length > 0) ? userChallenge.challenges :
                    (userChallenge?.text ? [userChallenge.text] : challenges.filter(c => c.trim()))
                  ).map((challenge, index) => (
                    <div key={index} className="p-3 sm:p-4 bg-primary-50 border border-primary-200 rounded-xl">
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary-500 text-white text-sm font-bold rounded-full flex items-center justify-center">
                          {index + 1}
                        </span>
                        <p className="text-sm sm:text-base text-neutral-900 leading-relaxed whitespace-pre-wrap flex-1">
                          {challenge || 'No response recorded'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {userChallenge?.timestamp && (
                  <p className="text-xs text-neutral-500 mt-2">
                    Submitted on {formatActualDateTime(userChallenge.timestamp)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-800">What's next?</p>
                <p className="text-sm text-blue-700">Your trainer will provide personalized guidance and support.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Input View - Clean Design
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBackToList && (
                <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="flex items-center">
                <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">Live</span>
              </div>
              {sessionCode && (
                <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                  <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Type Heading */}
      <div className="bg-primary-50 px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-primary-600 tracking-wide">CHALLENGES</h2>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Side - Challenge Input */}
          <div className="flex-1 order-2 lg:order-1">
            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3 sm:mb-4 leading-tight">
              {activity.title || 'Share Your Challenge'}
            </h1>

            <p className="text-base sm:text-lg text-neutral-600 mb-6 sm:mb-8">
              {activity.question || "Describe any challenges or difficulties you're experiencing."}
            </p>

            {/* Challenge Input Card */}
            <div className="bg-neutral-50 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900">Add Your Challenges</h3>
                    <p className="text-sm text-neutral-500">Add each challenge separately for better clarity</p>
                  </div>
                </div>
                <span className="text-sm text-neutral-500">{challenges.length}/10</span>
              </div>

              {/* Multiple Challenge Text Boxes */}
              <div className="space-y-4">
                {challenges.map((challenge, index) => (
                  <div key={index} className="relative">
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-7 h-7 bg-amber-500 text-white text-sm font-bold rounded-full flex items-center justify-center mt-3">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <textarea
                          value={challenge}
                          onChange={(e) => updateChallenge(index, e.target.value)}
                          placeholder={index === 0 ? "Describe your first challenge..." : "Add another challenge..."}
                          rows={3}
                          maxLength={500}
                          className="w-full px-4 py-3 text-neutral-900 bg-white border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 resize-none transition-all"
                        />
                        <p className="text-xs text-neutral-400 mt-1">{challenge.length}/500 characters</p>
                      </div>
                      {challenges.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeChallenge(index)}
                          className="flex-shrink-0 w-8 h-8 mt-3 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-colors"
                          title="Remove this challenge"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Challenge Button */}
              {challenges.length < 10 && (
                <button
                  type="button"
                  onClick={addChallenge}
                  className="mt-4 w-full py-3 border-2 border-dashed border-amber-300 hover:border-amber-500 bg-amber-50 hover:bg-amber-100 text-amber-700 font-medium rounded-xl flex items-center justify-center gap-2 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Another Challenge
                </button>
              )}
            </div>

            {/* Submit Button */}
            <div className="mb-8">
              <button
                onClick={handleSubmit}
                disabled={getNonEmptyChallenges().length === 0}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
              >
                Submit {getNonEmptyChallenges().length > 1 ? `${getNonEmptyChallenges().length} Challenges` : 'Challenge'}
              </button>
            </div>

            {/* Privacy Info */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-800">Your Privacy</p>
                  <p className="text-sm text-blue-700">Your challenge will be shared with the trainer for personalized guidance and support.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Timer & Info */}
          <div className="w-full lg:w-64 flex-shrink-0 order-1 lg:order-2">
            <div className="lg:sticky lg:top-8">
              {/* Circular Timer - Horizontal on mobile */}
              {timerEnabled && (
              <div className="flex flex-row lg:flex-col items-center gap-4 lg:gap-0 mb-6 lg:mb-8 p-4 lg:p-0 bg-neutral-50 lg:bg-transparent rounded-xl lg:rounded-none">
                <div className="relative w-20 h-20 lg:w-24 lg:h-24">
                  {/* Background circle */}
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#e5e7eb"
                      strokeWidth="6"
                      fill="none"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#ff8a00"
                      strokeWidth="6"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - (timeRemaining / maxTime))}`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  {/* Timer text */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg lg:text-2xl font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                  </div>
                </div>
                <span className="text-sm text-neutral-500 lg:mt-2">Time Remaining</span>
              </div>
              )}

              {/* Activity Info - Hidden on mobile, shown in timer section */}
              <div className="hidden lg:block bg-neutral-50 rounded-xl p-4">
                <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Activity Info
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Session:</span>
                    <span className="font-medium text-neutral-700">{sessionCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Type:</span>
                    <span className="font-medium text-neutral-700">Challenges</span>
                  </div>
                </div>
              </div>

              {/* Tips - Hidden on mobile */}
              <div className="hidden lg:block mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="flex gap-2">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-amber-800">Tips</p>
                    <p className="text-xs text-amber-700 mt-1">Be specific about what you're struggling with so your trainer can provide targeted help.</p>
                  </div>
                </div>
              </div>

              {/* Confidential Notice - Hidden on mobile */}
              <div className="hidden lg:flex mt-4 items-center gap-2 text-sm text-neutral-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Shared with trainer only</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengesActivity;
